import { DATE_FORMATS, parseFlexibleDate } from '../../../../../../utils/dateUtils';
import { SortConfig, SORT_DIRECTIONS } from '../../../../../../utils/constants/commonConstants';
import { DRILL_DOWN_SORT_FIELDS } from './constants';
import { BankTransaction } from './types';

/** "YYYY-MM" month keys, both required — matches useDrillDownDateRange's `applied`-adjacent
 * shape. Empty strings mean no range applied (show every group, Undated included). */
export interface DrillDownMonthRange {
    startMonth: string;
    endMonth: string;
}

export interface MonthGroup {
    monthKey: string; // sortable "YYYY-MM"; '' for unparseable dates
    monthLabel: string; // "Jan 2026"; 'Undated' for unparseable dates — used in subtotal rows
    tabLabel: string; // "Jan 26" (MMM YY); 'Undated' for unparseable dates — used in tab buttons
    transactions: BankTransaction[];
    credits: number;
    debits: number;
}

export interface GroupedTransactions {
    groups: MonthGroup[];
    grandCredits: number;
    grandDebits: number;
    grandNet: number;
}

function toNumber(value: number | string): number {
    const n = typeof value === 'string' ? parseFloat(value) : value;
    return Number.isFinite(n) ? n : 0;
}

function creditOf(txn: BankTransaction): number {
    return txn.transactionType === 'debit' ? 0 : Math.abs(toNumber(txn.amount));
}

function debitOf(txn: BankTransaction): number {
    return txn.transactionType === 'debit' ? Math.abs(toNumber(txn.amount)) : 0;
}

export function groupTransactionsByMonth(transactions: BankTransaction[]): GroupedTransactions {
    const byMonth = new Map<string, MonthGroup>();
    let grandCredits = 0;
    let grandDebits = 0;

    for (const txn of transactions) {
        const parsed = parseFlexibleDate(txn.date);
        const monthKey = parsed ? parsed.format('YYYY-MM') : '';
        const monthLabel = parsed ? parsed.format(DATE_FORMATS.MONTH_SHORT_YEAR) : 'Undated';
        const tabLabel = parsed ? parsed.format('MMM YY') : 'Undated';

        let group = byMonth.get(monthKey);
        if (!group) {
            group = { monthKey, monthLabel, tabLabel, transactions: [], credits: 0, debits: 0 };
            byMonth.set(monthKey, group);
        }
        const credit = creditOf(txn);
        const debit = debitOf(txn);
        group.transactions.push(txn);
        group.credits += credit;
        group.debits += debit;
        grandCredits += credit;
        grandDebits += debit;
    }

    const groups = [...byMonth.values()].sort((a, b) => a.monthKey.localeCompare(b.monthKey));
    return { groups, grandCredits, grandDebits, grandNet: grandCredits - grandDebits };
}

export type DrillDownDisplayRow = { kind: 'txn'; txn: BankTransaction } | { kind: 'subtotal'; group: MonthGroup };

export interface DrillDownView {
    visibleGroups: MonthGroup[];
    /** <tbody> content in render order, EXCLUDING the grand total (which the component renders itself). */
    rows: DrillDownDisplayRow[];
    credits: number;
    debits: number;
    net: number;
}

/** Sort key for one transaction. An unparseable date keys to -Infinity so undated rows sort where the Undated band already sits in the default view. */
function sortKeyOf(txn: BankTransaction, field: string): number {
    if (field === DRILL_DOWN_SORT_FIELDS.CREDITS) return creditOf(txn);
    if (field === DRILL_DOWN_SORT_FIELDS.DEBITS) return debitOf(txn);
    if (field === DRILL_DOWN_SORT_FIELDS.DATE) return parseFlexibleDate(txn.date)?.valueOf() ?? Number.NEGATIVE_INFINITY;
    return 0; // unrecognised field: every key ties, so the index tiebreak leaves the default order intact
}

/**
 * Explicit comparison, NOT `a - b`. Two undated rows both key to -Infinity, and
 * `-Infinity - -Infinity` is NaN — a comparator returning NaN makes Array#sort unspecified.
 */
function compareNumbers(a: number, b: number): number {
    if (a === b) return 0;
    return a < b ? -1 : 1;
}

/**
 * Filter first, then sort, so adjusting the Time Period never scrambles the chosen ordering. Unsorted keeps the grouped month bands; sorted flattens to one globally-ordered list with no subtotals.
 * Never mutates `grouped` — an in-place sort would destroy the default order for the lifetime of the caller's memo.
 */
export function buildDrillDownView(
    grouped: GroupedTransactions,
    range: DrillDownMonthRange,
    sortConfig: SortConfig | undefined,
): DrillDownView {
    const { groups } = grouped;

    // Both months required — a one-sided range can't bound a filter. A range excludes Undated (monthKey ''), since a dateless item can't fall inside a date range.
    const rangeApplied = !!range.startMonth && !!range.endMonth;
    const visibleGroups = rangeApplied
        ? groups.filter((g) => g.monthKey && g.monthKey >= range.startMonth && g.monthKey <= range.endMonth)
        : groups;

    const credits = visibleGroups.reduce((s, g) => s + g.credits, 0);
    const debits = visibleGroups.reduce((s, g) => s + g.debits, 0);

    let rows: DrillDownDisplayRow[];

    if (!sortConfig?.direction) {
        rows = [];
        for (const group of visibleGroups) {
            for (const txn of group.transactions) rows.push({ kind: 'txn', txn });
            rows.push({ kind: 'subtotal', group });
        }
    } else {
        const ascending = sortConfig.direction === SORT_DIRECTIONS.ASC;
        // Decorate-sort-undecorate: each date parses once, not O(n log n) times. Flatten and decorate
        // in one pass rather than two full-array traversals.
        let index = 0;
        const decorated = visibleGroups.flatMap((group) =>
            group.transactions.map((txn) => ({ txn, index: index++, key: sortKeyOf(txn, sortConfig.field) })),
        );

        decorated.sort((a, b) => {
            const diff = compareNumbers(a.key, b.key);
            if (diff !== 0) return ascending ? diff : -diff;
            return a.index - b.index; // ties keep default order in BOTH directions, without relying on sort stability
        });

        rows = decorated.map(({ txn }) => ({ kind: 'txn', txn }));
    }

    return { visibleGroups, rows, credits, debits, net: credits - debits };
}
